import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { submitChecklist, submitToolboxTalk } from './api/client';
import { checklistItems, CHECKLIST_ID, CHECKLIST_VERSION } from './data/checklist';
import { useOutboxSync } from './hooks/useOutboxSync';
import { useToolboxOutboxSync } from './hooks/useToolboxOutboxSync';
import { addToOutbox } from './storage/outbox';
import { addToolboxToOutbox } from './storage/toolboxOutbox';
import { getOperatorProfile, saveOperatorProfile } from './storage/operator';
import {
  ChecklistItemResult,
  ChecklistResponseState,
  ChecklistStatus,
  OperatorProfile,
  SubmissionPayload,
  ToolboxTalkPayload
} from './types';
import './App.css';

type ViewState =
  | 'setup'
  | 'home'
  | 'checklist'
  | 'summary'
  | 'toolbox-details'
  | 'toolbox-notes'
  | 'toolbox-attendees'
  | 'toolbox-summary';

type ResponseMap = Record<string, ChecklistResponseState>;

const buildInitialResponses = (): ResponseMap =>
  Object.fromEntries(
    checklistItems.map((item) => [item.id, { status: null, note: '' } as ChecklistResponseState])
  );

const LAST_TOOLBOX_ATTENDEES_KEY = 'toolbox-last-attendees';

function App() {
  const [view, setView] = useState<ViewState>('setup');
  const [operator, setOperator] = useState<OperatorProfile | null>(null);
  const [responses, setResponses] = useState<ResponseMap>(() => buildInitialResponses());
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<{ tone: 'success' | 'info' | 'error'; message: string } | null>(
    null
  );
  const [isOnline, setIsOnline] = useState<boolean>(() =>
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const { pendingCount, isSyncing, lastSyncError, pendingSince, syncOutbox } = useOutboxSync();
  const {
    pendingCount: toolboxPendingCount,
    pendingSince: toolboxPendingSince,
    isSyncing: toolboxIsSyncing,
    lastSyncError: toolboxLastSyncError,
    syncOutbox: syncToolboxOutbox
  } = useToolboxOutboxSync();
  const stepCardRef = useRef<HTMLElement | null>(null);
  const autoAdvanceRef = useRef<number | null>(null);
  const [stepError, setStepError] = useState<string | null>(null);
  const [confirmExit, setConfirmExit] = useState(false);
  const [pendingReturnToSummary, setPendingReturnToSummary] = useState(false);
  const [summaryFilters, setSummaryFilters] = useState<ChecklistStatus[]>(['PASS', 'FAIL', 'MISSING']);
  const summaryEndRef = useRef<HTMLDivElement | null>(null);
  const [toolboxForm, setToolboxForm] = useState({
    site: '',
    supervisor: '',
    meetingDate: '',
    previousUpdate: '',
    hazards: '',
    safetyAlerts: '',
    attendeeInput: '',
    attendeeRoleInput: '',
    attendees: [] as { name: string; role?: string }[]
  });
  const [toolboxSubmitting, setToolboxSubmitting] = useState(false);
  const [toolboxError, setToolboxError] = useState<string | null>(null);
  const [toolboxConfirmExit, setToolboxConfirmExit] = useState(false);
  const [lastAttendees, setLastAttendees] = useState<{ name: string; role?: string }[]>([]);

  useEffect(() => {
    const stored = getOperatorProfile();
    if (stored) {
      setOperator(stored);
      setView('home');
    }
  }, []);

  useEffect(() => {
    const handleOnlineStatus = () => setIsOnline(typeof navigator !== 'undefined' ? navigator.onLine : true);
    window.addEventListener('online', handleOnlineStatus);
    window.addEventListener('offline', handleOnlineStatus);
    try {
      const saved = localStorage.getItem(LAST_TOOLBOX_ATTENDEES_KEY);
      if (saved) {
        setLastAttendees(JSON.parse(saved) as { name: string; role?: string }[]);
      }
    } catch {
      setLastAttendees([]);
    }
    return () => {
      window.removeEventListener('online', handleOnlineStatus);
      window.removeEventListener('offline', handleOnlineStatus);
    };
  }, []);

  useEffect(() => {
    if (view === 'checklist' && stepCardRef.current) {
      stepCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [currentIndex, view]);

  useEffect(() => {
    return () => {
      if (autoAdvanceRef.current != null) {
        window.clearTimeout(autoAdvanceRef.current);
      }
    };
  }, []);

  const totalSteps = checklistItems.length;
  const activeItem = checklistItems[currentIndex];
  const currentResponse = activeItem ? responses[activeItem.id] ?? { status: null, note: '' } : null;

  const answeredCount = useMemo(
    () =>
      checklistItems.reduce((count, item) => {
        const response = responses[item.id];
        return response?.status ? count + 1 : count;
      }, 0),
    [responses]
  );

  const completedItems: ChecklistItemResult[] = useMemo(() => {
    return checklistItems.map((item) => {
      const response = responses[item.id];
      const status = response?.status ?? 'MISSING';
      return {
        id: item.id,
        ref: item.ref,
        label: item.action || item.check || `Ref ${item.ref}`,
        action: item.action,
        check: item.check,
        status,
        note: status === 'FAIL' ? response?.note?.trim() || '' : undefined
      } satisfies ChecklistItemResult;
    });
  }, [responses]);

  const filteredSummaryItems = useMemo(
    () => completedItems.filter((item) => summaryFilters.includes(item.status)),
    [completedItems, summaryFilters]
  );

  const handleProfileSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const name = (formData.get('name') as string)?.trim();
    const site = (formData.get('site') as string)?.trim();
    if (!name) {
      return;
    }
    const profile: OperatorProfile = { name, site: site || undefined };
    saveOperatorProfile(profile);
    setOperator(profile);
    setView('home');
  };

  const handleStartCheck = () => {
    setResponses(buildInitialResponses());
    setCurrentIndex(0);
    setView('checklist');
    setFeedback(null);
    setStepError(null);
    setPendingReturnToSummary(false);
    setConfirmExit(false);
  };

  const handleStartToolbox = () => {
    setToolboxForm({
      site: operator?.site ?? '',
      supervisor: operator?.name ?? '',
      meetingDate: new Date().toISOString().slice(0, 10),
      previousUpdate: '',
      hazards: '',
      safetyAlerts: '',
      attendeeInput: '',
      attendeeRoleInput: '',
      attendees: []
    });
    setToolboxError(null);
    setToolboxConfirmExit(false);
    setView('toolbox-details');
  };

  const handleStatusChange = (id: string, status: ChecklistStatus) => {
    setResponses((prev) => ({
      ...prev,
      [id]: {
        status,
        note: status === 'FAIL' ? prev[id]?.note ?? '' : ''
      }
    }));
    const isCurrent = checklistItems[currentIndex]?.id === id;
    if (status === 'PASS' && isCurrent) {
      scheduleAdvance();
    } else if (status === 'FAIL' && isCurrent) {
      setStepError('Please describe failed checks before moving on.');
    } else if (status !== 'FAIL') {
      setStepError(null);
    }
  };

  const handleNoteChange = (id: string, note: string) => {
    setResponses((prev) => ({
      ...prev,
      [id]: {
        status: prev[id]?.status ?? 'FAIL',
        note
      }
    }));
    const isCurrent = checklistItems[currentIndex]?.id === id;
    if (note.trim().length > 0 && isCurrent) {
      setStepError(null);
    }
  };

  const jumpToStep = (targetId: string, fromSummary = false) => {
    const newIndex = checklistItems.findIndex((item) => item.id === targetId);
    if (newIndex === -1) {
      return;
    }
    goToStep(newIndex);
    if (fromSummary) {
      setPendingReturnToSummary(true);
      setView('checklist');
    }
  };

  const moveToSummary = () => {
    clearAutoAdvance();
    setStepError(null);
    setView('summary');
    setPendingReturnToSummary(false);
  };

  const handleSubmitChecklist = async () => {
    if (!operator) {
      return;
    }

    const payload: SubmissionPayload = {
      operatorName: operator.name,
      operatorSite: operator.site,
      submittedAt: new Date().toISOString(),
      checklistId: CHECKLIST_ID,
      checklistVersion: CHECKLIST_VERSION,
      items: completedItems
    };

    setIsSubmitting(true);

    const queueSubmission = async (message: string) => {
      await addToOutbox(payload);
      setFeedback({ tone: 'info', message });
      setView('home');
      setIsSubmitting(false);
    };

    if (!navigator.onLine) {
      await queueSubmission('You appear to be offline. Submission queued for automatic send.');
      return;
    }

    try {
      await submitChecklist(payload);
      const formatted = formatDisplayTimestamp(payload.submittedAt);
      setFeedback({
        tone: 'success',
        message: `Checklist submitted at ${formatted} (v${CHECKLIST_VERSION}).`
      });
      setView('home');
      setConfirmExit(false);
      await syncOutbox();
    } catch (error) {
      console.warn('Submission failed, queueing for retry', error);
      await queueSubmission('Connection issue detected. Submission saved locally and will retry.');
      return;
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetProfile = () => {
    setView('setup');
  };

  const clearAutoAdvance = () => {
    if (autoAdvanceRef.current != null) {
      window.clearTimeout(autoAdvanceRef.current);
      autoAdvanceRef.current = null;
    }
  };

  const goToStep = (index: number) => {
    clearAutoAdvance();
    setCurrentIndex(Math.min(Math.max(index, 0), totalSteps - 1));
    setConfirmExit(false);
  };

  const advanceForward = () => {
    setStepError(null);
    if (currentIndex >= totalSteps - 1) {
      moveToSummary();
    } else {
      goToStep(currentIndex + 1);
    }
  };

  const scheduleAdvance = () => {
    clearAutoAdvance();
    autoAdvanceRef.current = window.setTimeout(() => {
      autoAdvanceRef.current = null;
      advanceForward();
    }, 220);
  };

  const ensureFailHasNote = () => {
    if (currentResponse?.status === 'FAIL' && !currentResponse.note.trim()) {
      setStepError('Please describe failed checks before moving on.');
      return false;
    }
    setStepError(null);
    return true;
  };

  const handleNext = () => {
    if (currentIndex >= totalSteps - 1) {
      return;
    }
    if (!ensureFailHasNote()) {
      return;
    }
    advanceForward();
    setConfirmExit(false);
  };

  const handlePrevious = () => {
    setStepError(null);
    goToStep(currentIndex - 1);
    setConfirmExit(false);
  };

  const handleSummaryTransition = () => {
    if (!ensureFailHasNote()) {
      return;
    }
    moveToSummary();
    setConfirmExit(false);
  };

  const handleNoteBlur = (id: string) => {
    const isCurrent = checklistItems[currentIndex]?.id === id;
    if (!isCurrent) {
      return;
    }
    const response = responses[id];
    if (response?.status === 'FAIL' && response.note.trim()) {
      scheduleAdvance();
    }
  };

  const handleToolboxNext = () => {
    if (view === 'toolbox-details') {
      if (!toolboxForm.site.trim() || !toolboxForm.supervisor.trim() || !toolboxForm.meetingDate.trim()) {
        setToolboxError('Site/vessel, supervisor, and date are required.');
        return;
      }
      setToolboxError(null);
      setView('toolbox-notes');
      return;
    }
    if (view === 'toolbox-notes') {
      setToolboxError(null);
      setView('toolbox-attendees');
      return;
    }
    if (view === 'toolbox-attendees') {
      if (toolboxForm.attendees.length === 0) {
        setToolboxError('Add at least one attendee.');
        return;
      }
      setToolboxError(null);
      setView('toolbox-summary');
    }
  };

  const handleToolboxPrevious = () => {
    if (view === 'toolbox-notes') {
      setView('toolbox-details');
    } else if (view === 'toolbox-attendees') {
      setView('toolbox-notes');
    } else if (view === 'toolbox-summary') {
      setView('toolbox-attendees');
    }
    setToolboxConfirmExit(false);
  };

  const handleAddAttendee = () => {
    const name = toolboxForm.attendeeInput.trim();
    const role = toolboxForm.attendeeRoleInput.trim();
    if (!name) return;
    setToolboxForm((prev) => ({
      ...prev,
      attendees: [...prev.attendees, { name, role: role || undefined }],
      attendeeInput: '',
      attendeeRoleInput: ''
    }));
    setToolboxError(null);
  };

  const handleRemoveAttendee = (name: string) => {
    setToolboxForm((prev) => ({
      ...prev,
      attendees: prev.attendees.filter((a) => a.name !== name)
    }));
  };

  const handleLoadLastAttendees = () => {
    if (lastAttendees.length === 0) {
      return;
    }
    setToolboxForm((prev) => ({
      ...prev,
      attendees: lastAttendees
    }));
    setToolboxError(null);
  };

  const handleSubmitToolbox = async () => {
    if (!toolboxForm.site.trim() || !toolboxForm.supervisor.trim()) {
      setToolboxError('Site/vessel and supervisor are required.');
      return;
    }
    if (toolboxForm.attendees.length === 0) {
      setToolboxError('Add at least one attendee.');
      return;
    }
    const payload: ToolboxTalkPayload = {
      operatorName: operator?.name ?? 'Unknown operator',
      operatorSite: operator?.site,
      supervisor: toolboxForm.supervisor.trim(),
      site: toolboxForm.site.trim(),
      meetingDate: toolboxForm.meetingDate.trim(),
      previousUpdate: toolboxForm.previousUpdate.trim(),
      hazards: toolboxForm.hazards.trim(),
      safetyAlerts: toolboxForm.safetyAlerts.trim(),
      attendees: toolboxForm.attendees.map(({ name, role }) => ({ name, role })),
      submittedAt: new Date().toISOString()
    };
    setToolboxSubmitting(true);
    setToolboxError(null);
    try {
      if (!navigator.onLine) {
        await addToolboxToOutbox(payload);
        setFeedback({ tone: 'info', message: 'You appear to be offline. Toolbox talk queued.' });
        setView('home');
      } else {
        await submitToolboxTalk(payload);
        const formatted = formatDisplayTimestamp(payload.submittedAt);
        setFeedback({
          tone: 'success',
          message: `Toolbox talk submitted at ${formatted}.`
        });
        setView('home');
        await syncToolboxOutbox();
      }
      setToolboxConfirmExit(false);
      try {
        localStorage.setItem(LAST_TOOLBOX_ATTENDEES_KEY, JSON.stringify(toolboxForm.attendees));
        setLastAttendees(toolboxForm.attendees);
      } catch {
        // ignore storage errors
      }
    } catch (error) {
      console.warn('Toolbox submission failed, queueing for retry', error);
      await addToolboxToOutbox(payload);
      setFeedback({ tone: 'info', message: 'Connection issue detected. Toolbox talk queued for retry.' });
      setView('home');
    } finally {
      setToolboxSubmitting(false);
    }
  };

  const toggleSummaryFilter = (status: ChecklistStatus) => {
    setSummaryFilters((prev) => {
      const has = prev.includes(status);
      if (has && prev.length === 1) {
        return prev;
      }
      if (has) {
        return prev.filter((s) => s !== status);
      }
      return [...prev, status];
    });
  };

  const scrollToSummaryEnd = () => {
    summaryEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="app-shell">
      <header className="app-header">
        <div>
          <p className="eyebrow">Daily Function Check - CableFish</p>
          <h1>Daily Function Check</h1>
          <p className="subhead">Designed for quick offshore validations with offline support.</p>
        </div>
        {operator && (
          <div className="operator-card">
            <div className="status-pill" aria-live="polite">
              <span className={isOnline ? 'dot online' : 'dot offline'} aria-hidden="true" />
              <span>{isOnline ? 'Online' : 'Offline (queued)'}</span>
            </div>
            <p className="label">Operator</p>
            <p className="operator-name">{operator.name}</p>
            {operator.site && <p className="muted">{operator.site}</p>}
          </div>
        )}
      </header>

      <main>
        {!isOnline && (
          <div className="banner warning">Offline mode: submissions will be queued until back online.</div>
        )}

        {pendingCount > 0 && (
          <div className="banner info">
            Pending submissions: {pendingCount} {isSyncing ? '(syncing...)' : ''}
            {pendingSince && (
              <span className="muted"> — oldest queued {formatDisplayTimestamp(pendingSince)}</span>
            )}
            {lastSyncError && <span className="muted"> — {lastSyncError}</span>}
            <button
              type="button"
              className="inline-button"
              onClick={() => syncOutbox()}
              disabled={!isOnline || isSyncing}
            >
              Sync now
            </button>
          </div>
        )}
        {toolboxPendingCount > 0 && (
          <div className="banner info">
            Pending toolbox talks: {toolboxPendingCount} {toolboxIsSyncing ? '(syncing...)' : ''}
            {toolboxPendingSince && (
              <span className="muted"> — oldest queued {formatDisplayTimestamp(toolboxPendingSince)}</span>
            )}
            {toolboxLastSyncError && <span className="muted"> — {toolboxLastSyncError}</span>}
            <button
              type="button"
              className="inline-button"
              onClick={() => syncToolboxOutbox()}
              disabled={!isOnline || toolboxIsSyncing}
            >
              Sync now
            </button>
          </div>
        )}

        {feedback && (
          <div className={`banner ${feedback.tone}`}>{feedback.message}</div>
        )}

        {view === 'setup' && (
          <section className="card">
            <h2>Who is performing the check?</h2>
            <form className="form-stack" onSubmit={handleProfileSubmit}>
              <label>
                <span>Operator name</span>
                <input type="text" name="name" required defaultValue={operator?.name ?? ''} />
              </label>
              <label>
                <span>Site / vessel (optional)</span>
                <input type="text" name="site" defaultValue={operator?.site ?? ''} />
              </label>
              <button type="submit" className="primary">Save and continue</button>
            </form>
          </section>
        )}

        {view === 'home' && (
          <section className="card card-home">
            <div className="card-header">
              <h2>Ready to start?</h2>
              <div className="card-actions">
                <button type="button" className="secondary ghost" onClick={resetProfile}>
                  Update details
                </button>
              </div>
            </div>
            <p>Run through the full checklist once per shift. Results send automatically via email.</p>
            <div className="home-actions">
              <button className="primary" type="button" onClick={handleStartCheck}>
                Start Daily Function Check
              </button>
              <button className="primary ghost" type="button" onClick={handleStartToolbox}>
                Start Toolbox Talk
              </button>
            </div>
            <div className="coming-soon">
              <p className="muted-label">Coming soon</p>
              <div className="pill-grid">
                <button type="button" className="pill ghost" aria-disabled="true">
                  Spares inventory
                </button>
                <button type="button" className="pill ghost" aria-disabled="true">
                  Common issues and solutions
                </button>
                <button type="button" className="pill ghost" aria-disabled="true">
                  Make a suggestion
                </button>
              </div>
            </div>
          </section>
        )}

        {view === 'checklist' && activeItem && (
          <section className="card step-card" ref={stepCardRef}>
            <header className="step-header">
              <div className="step-header-top">
                <p className="step-eyebrow">
                  Step {currentIndex + 1} of {totalSteps}
                </p>
                <div className="top-nav-buttons">
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => {
                      clearAutoAdvance();
                      setStepError(null);
                      if (confirmExit) {
                        setView('home');
                        setPendingReturnToSummary(false);
                        setConfirmExit(false);
                      } else {
                        setConfirmExit(true);
                      }
                    }}
                  >
                    {confirmExit ? 'Confirm exit' : 'Exit'}
                  </button>
                  {pendingReturnToSummary && (
                    <button
                      type="button"
                      className="secondary"
                      onClick={() => {
                        clearAutoAdvance();
                        setPendingReturnToSummary(false);
                        setView('summary');
                      }}
                    >
                      Summary
                    </button>
                  )}
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => {
                      handlePrevious();
                      setConfirmExit(false);
                    }}
                    disabled={currentIndex === 0}
                  >
                    Previous
                  </button>
                  {currentIndex < totalSteps - 1 && (
                    <button
                      type="button"
                      className="primary next-button"
                      onClick={() => {
                        handleNext();
                        setConfirmExit(false);
                      }}
                    >
                      Next
                    </button>
                  )}
                  {currentIndex === totalSteps - 1 && (
                    <button
                      type="button"
                      className="primary next-button"
                      onClick={() => {
                        handleSummaryTransition();
                        setConfirmExit(false);
                      }}
                    >
                      Review summary
                    </button>
                  )}
                </div>
              </div>
              <h2>Step {activeItem.ref} — {getItemTitle(activeItem)}</h2>
            </header>

            <div className="item-actions">
              <button
                type="button"
                className={currentResponse?.status === 'FAIL' ? 'option selected danger' : 'option danger'}
                onClick={() => handleStatusChange(activeItem.id, 'FAIL')}
              >
                Fail
              </button>
              <button
                type="button"
                className={currentResponse?.status === 'PASS' ? 'option selected pass' : 'option pass'}
                onClick={() => handleStatusChange(activeItem.id, 'PASS')}
              >
                Pass
              </button>
            </div>

            <div className="step-body">
              <StepBlock label="Action" value={activeItem.action} />
              <StepBlock label="Check" value={activeItem.check} />
              <StepBlock label="Initials / notes" value={activeItem.init} />
            </div>

            {currentResponse?.status === 'FAIL' && (
              <label className="note-field">
                <span>Describe the issue</span>
                <textarea
                  value={currentResponse.note}
                  onChange={(event) => handleNoteChange(activeItem.id, event.target.value)}
                  onBlur={() => handleNoteBlur(activeItem.id)}
                  required
                  rows={3}
                />
              </label>
            )}

            <div className="step-meta">
              <div className="progress">
                <div className="progress-bar" style={{ width: `${(answeredCount / totalSteps) * 100}%` }} />
              </div>
              <p className="muted">
                {answeredCount} of {totalSteps} steps answered
              </p>
            </div>

            <div className="step-navigation">{stepError && <p className="validation-error">{stepError}</p>}</div>
          </section>
        )}

        {view === 'toolbox-details' && (
          <section className="card step-card">
            <header className="step-header">
              <div className="step-header-top">
                <p className="step-eyebrow">Step 1 of 3</p>
              <div className="top-nav-buttons">
                <div className="status-pill inline" aria-live="polite">
                  <span className={isOnline ? 'dot online' : 'dot offline'} aria-hidden="true" />
                  <span>{isOnline ? 'Online' : 'Offline (queued)'}</span>
                </div>
                <button
                  type="button"
                  className="secondary"
                  onClick={() => {
                    if (toolboxConfirmExit) {
                        setView('home');
                        setToolboxConfirmExit(false);
                      } else {
                        setToolboxConfirmExit(true);
                      }
                    }}
                  >
                    {toolboxConfirmExit ? 'Confirm exit' : 'Exit'}
                  </button>
                  <button type="button" className="secondary" disabled>
                    Previous
                  </button>
                  <button type="button" className="primary next-button" onClick={handleToolboxNext}>
                    Next
                  </button>
                </div>
              </div>
              <h2>Toolbox Talk — Details</h2>
            </header>
            <div className="form-stack">
              <label>
                <span>Site / vessel</span>
                <input
                  type="text"
                  value={toolboxForm.site}
                  onChange={(e) => setToolboxForm((prev) => ({ ...prev, site: e.target.value }))}
                  required
                />
              </label>
              <label>
                <span>Supervisor</span>
                <input
                  type="text"
                  value={toolboxForm.supervisor}
                  onChange={(e) => setToolboxForm((prev) => ({ ...prev, supervisor: e.target.value }))}
                  required
                />
              </label>
              <label>
                <span>Date</span>
                <input
                  type="date"
                  value={toolboxForm.meetingDate}
                  onChange={(e) => setToolboxForm((prev) => ({ ...prev, meetingDate: e.target.value }))}
                  required
                />
              </label>
            </div>
            {toolboxError && <p className="validation-error">{toolboxError}</p>}
          </section>
        )}

        {view === 'toolbox-notes' && (
          <section className="card step-card">
            <header className="step-header">
              <div className="step-header-top">
                <p className="step-eyebrow">Step 2 of 3</p>
              <div className="top-nav-buttons">
                <div className="status-pill inline" aria-live="polite">
                  <span className={isOnline ? 'dot online' : 'dot offline'} aria-hidden="true" />
                  <span>{isOnline ? 'Online' : 'Offline (queued)'}</span>
                </div>
                <button
                  type="button"
                  className="secondary"
                  onClick={() => {
                    if (toolboxConfirmExit) {
                        setView('home');
                        setToolboxConfirmExit(false);
                      } else {
                        setToolboxConfirmExit(true);
                      }
                    }}
                  >
                    {toolboxConfirmExit ? 'Confirm exit' : 'Exit'}
                  </button>
                  <button type="button" className="secondary" onClick={handleToolboxPrevious}>
                    Previous
                  </button>
                  <button type="button" className="primary next-button" onClick={handleToolboxNext}>
                    Next
                  </button>
                </div>
              </div>
              <h2>Toolbox Talk — Discussion Points</h2>
            </header>
            <div className="form-stack">
              <label>
                <span>Update of previous safety meeting / toolbox talk</span>
                <textarea
                  placeholder="What was covered? Any outstanding items?"
                  value={toolboxForm.previousUpdate}
                  onChange={(e) => setToolboxForm((prev) => ({ ...prev, previousUpdate: e.target.value }))}
                  rows={3}
                />
              </label>
              <label>
                <span>Recent hazards / issues identified onsite</span>
                <textarea
                  placeholder="Discuss any recent problems (e.g., noncompliance with site rules, recent incidents or near misses). Encourage staff to give feedback."
                  value={toolboxForm.hazards}
                  onChange={(e) => setToolboxForm((prev) => ({ ...prev, hazards: e.target.value }))}
                  rows={3}
                />
              </label>
              <label>
                <span>Safety alerts</span>
                <textarea
                  placeholder="Discuss recent safety alerts for the upcoming work."
                  value={toolboxForm.safetyAlerts}
                  onChange={(e) => setToolboxForm((prev) => ({ ...prev, safetyAlerts: e.target.value }))}
                  rows={3}
                />
              </label>
            </div>
            {toolboxError && <p className="validation-error">{toolboxError}</p>}
          </section>
        )}

        {view === 'toolbox-attendees' && (
          <section className="card step-card">
            <header className="step-header">
              <div className="step-header-top">
                <p className="step-eyebrow">Step 3 of 3</p>
              <div className="top-nav-buttons">
                <div className="status-pill inline" aria-live="polite">
                  <span className={isOnline ? 'dot online' : 'dot offline'} aria-hidden="true" />
                  <span>{isOnline ? 'Online' : 'Offline (queued)'}</span>
                </div>
                <button
                  type="button"
                  className="secondary"
                  onClick={() => {
                    if (toolboxConfirmExit) {
                        setView('home');
                        setToolboxConfirmExit(false);
                      } else {
                        setToolboxConfirmExit(true);
                      }
                    }}
                  >
                    {toolboxConfirmExit ? 'Confirm exit' : 'Exit'}
                  </button>
                  <button type="button" className="secondary" onClick={handleToolboxPrevious}>
                    Previous
                  </button>
                  <button type="button" className="primary next-button" onClick={handleToolboxNext}>
                    Review summary
                  </button>
                </div>
              </div>
              <h2>Toolbox Talk — Attendees</h2>
            </header>
            <div className="form-stack">
              <label>
                <span>Add attendee</span>
                <div className="attendee-input">
                  <input
                    type="text"
                    value={toolboxForm.attendeeInput}
                    onChange={(e) => setToolboxForm((prev) => ({ ...prev, attendeeInput: e.target.value }))}
                    placeholder="Name or initials"
                  />
                  <input
                    type="text"
                    value={toolboxForm.attendeeRoleInput}
                    onChange={(e) => setToolboxForm((prev) => ({ ...prev, attendeeRoleInput: e.target.value }))}
                    placeholder="Role (e.g., Rigger, Supervisor)"
                  />
                  <button type="button" className="primary" onClick={handleAddAttendee}>
                    Add
                  </button>
                </div>
              </label>
              {toolboxForm.attendees.length > 0 && (
                <ul className="attendee-list">
                  {toolboxForm.attendees.map((person) => (
                    <li key={person.name + (person.role || '')} className="attendee-chip">
                      <span>{person.name}</span>
                      {person.role && <span className="chip-role">{person.role}</span>}
                      <button type="button" onClick={() => handleRemoveAttendee(person.name)} aria-label={`Remove ${person.name}`}>
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {lastAttendees.length > 0 && toolboxForm.attendees.length === 0 && (
                <button type="button" className="inline-button" onClick={handleLoadLastAttendees}>
                  Load last attendees
                </button>
              )}
            </div>
            {toolboxError && <p className="validation-error">{toolboxError}</p>}
          </section>
        )}

        {view === 'toolbox-summary' && (
          <section className="card">
            <div className="summary-header">
              <h2>Toolbox Talk Summary</h2>
            </div>
            <div className="summary-list toolbox">
              <div className="summary-item toolbox-card">
                <div>
                  <p className="item-title">Site / vessel</p>
                  <p className="item-description">{toolboxForm.site || 'Not provided'}</p>
                </div>
              </div>
              <div className="summary-item toolbox-card">
                <div>
                  <p className="item-title">Supervisor</p>
                  <p className="item-description">{toolboxForm.supervisor || 'Not provided'}</p>
                </div>
                <span className="chip success">Date {toolboxForm.meetingDate || 'N/A'}</span>
              </div>
              <div className="summary-item toolbox-card">
                <div>
                  <p className="item-title">Update of previous safety meeting / toolbox talk</p>
                  <p className="item-description">{toolboxForm.previousUpdate || 'Not provided'}</p>
                </div>
              </div>
              <div className="summary-item toolbox-card">
                <div>
                  <p className="item-title">Recent hazards / issues identified onsite</p>
                  <p className="item-description">{toolboxForm.hazards || 'Not provided'}</p>
                </div>
              </div>
              <div className="summary-item toolbox-card">
                <div>
                  <p className="item-title">Safety alerts</p>
                  <p className="item-description">{toolboxForm.safetyAlerts || 'Not provided'}</p>
                </div>
              </div>
              <div className="summary-item toolbox-card">
                <div>
                  <p className="item-title">Attendees</p>
                  <ul className="attendee-list static">
                    {toolboxForm.attendees.map((person) => (
                      <li key={person.name + (person.role || '')}>
                        <strong>{person.name}</strong>
                        {person.role && <span className="chip-role-inline"> — {person.role}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            {toolboxError && <p className="validation-error">{toolboxError}</p>}
            <div className="actions">
              <button type="button" className="secondary" onClick={handleToolboxPrevious}>
                Back
              </button>
              <button type="button" className="primary submit-button" onClick={handleSubmitToolbox} disabled={toolboxSubmitting}>
                {toolboxSubmitting ? 'Submitting…' : 'Submit toolbox talk'}
              </button>
            </div>
          </section>
        )}

        {view === 'summary' && (
          <section className="card">
            <div className="summary-header">
              <h2>Summary</h2>
              <div className="summary-actions-bar">
                <button type="button" className="secondary ghost" onClick={scrollToSummaryEnd}>
                  Jump to bottom
                </button>
              </div>
            </div>

            <div className="filter-row">
              <p className="muted-label">Filter</p>
              <div className="pill-grid">
                {(['PASS', 'FAIL', 'MISSING'] as ChecklistStatus[]).map((status) => (
                  <button
                    key={status}
                    type="button"
                    className={summaryFilters.includes(status) ? 'pill active' : 'pill ghost'}
                    onClick={() => toggleSummaryFilter(status)}
                  >
                    {status === 'PASS' ? 'Pass' : status === 'FAIL' ? 'Fail' : 'Missing'}
                  </button>
                ))}
              </div>
            </div>
            <div className="text-button-row">
              <button type="button" className="text-button" onClick={resetProfile}>
                Update details
              </button>
            </div>

            <ul className="summary-list">
              {filteredSummaryItems.map((item) => {
                const rowClass =
                  item.status === 'FAIL' ? 'summary-item fail' : item.status === 'MISSING' ? 'summary-item missing' : 'summary-item';
                const reviewButtonClass = item.status === 'MISSING' ? 'summary-action warning' : 'summary-action';
                return (
                <li key={item.id} className={rowClass}>
                  <div>
                    <p className="item-title">Step {item.ref} — {getItemTitle(item)}</p>
                    {item.check && <p className="item-description">{item.check}</p>}
                    {item.note && <p className="item-note">Note: {item.note}</p>}
                  </div>
                  <div className="summary-actions">
                    <span className={item.status === 'FAIL' ? 'chip danger' : item.status === 'MISSING' ? 'chip warning' : 'chip success'}>
                      {item.status}
                    </span>
                    {item.status !== 'PASS' && (
                      <button
                        type="button"
                        className={reviewButtonClass}
                        onClick={() => jumpToStep(item.id, true)}
                      >
                        Review step
                      </button>
                    )}
                  </div>
                </li>
              );})}
            </ul>
            <div ref={summaryEndRef} />
            <div className="actions">
              <button type="button" className="secondary" onClick={() => setView('checklist')}>
                Back
              </button>
              <button
                type="button"
                className="primary submit-button"
                onClick={handleSubmitChecklist}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting…' : 'Submit checklist'}
              </button>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}

export default App;

function formatDisplayTimestamp(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const formatted = date.toLocaleString('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'UTC'
  });
  return `${formatted} UTC`;
}

function StepBlock({ label, value }: { label: string; value?: string }) {
  if (!value) {
    return null;
  }

  return (
    <section className="step-block">
      <p className="step-block-label">{label}</p>
      <div className="step-block-content">{value}</div>
    </section>
  );
}

function getItemTitle(item: { ref: string; action?: string; check?: string }) {
  return (item.action && item.action.trim()) || (item.check && item.check.trim()) || `Item ${item.ref}`;
}
